import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_PRIMARY, NAV_ALL } from "../../config/site";

const Wordmark = () => (
  <Link to="/" className="flex items-center" data-testid="header-logo" aria-label="The Vantage Institute — home">
    <img
      src="/images/vantage-institute-logo.webp"
      alt="The Vantage Institute"
      width="56"
      height="56"
      loading="eager"
      fetchpriority="high"
      className="h-12 w-12 shrink-0 object-contain sm:h-16 sm:w-16"
      data-testid="header-logo-image"
    />
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 border-b border-platinum bg-white/95 backdrop-blur">
      <div className="container-x flex h-16 items-center justify-between gap-4 sm:h-20">
        <Wordmark />
        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-6">
            {NAV_PRIMARY.map((item) => (
              <li key={item.label}>
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  className={({ isActive }) =>
                    `font-sub text-[0.8rem] font-semibold uppercase tracking-[0.1em] transition-colors hover:text-gold-deep ${
                      isActive ? "text-gold-deep underline decoration-gold decoration-2 underline-offset-8" : "text-navy"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/learning" className="btn-gold hidden !min-h-[44px] whitespace-nowrap !px-5 !py-2 text-xs xl:inline-flex" data-testid="header-cta-find-path">
            Find Your Learning Path
          </Link>
          <button
            type="button"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center text-navy lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            data-testid="mobile-menu-open"
          >
            <Menu size={26} aria-hidden="true" />
          </button>
        </div>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[70] flex flex-col bg-navy lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-menu">
          <div className="container-x flex h-16 items-center justify-between">
            <img
              src="/images/vantage-institute-logo.webp"
              alt="The Vantage Institute"
              width="44"
              height="44"
              loading="eager"
              className="h-11 w-11 shrink-0 object-contain"
              data-testid="mobile-menu-logo-image"
            />
            <button
              type="button"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center text-white"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
              autoFocus
            >
              <X size={26} aria-hidden="true" />
            </button>
          </div>
          <nav aria-label="Mobile" className="container-x flex-1 overflow-y-auto py-6">
            <ul className="space-y-1">
              {NAV_ALL.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                    className="block border-b border-white/10 py-3.5 font-sub text-lg font-medium tracking-wide text-white transition-colors hover:text-gold"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-3 pb-8">
              <Link to="/learning" className="btn-gold w-full" data-testid="mobile-cta-find-path">
                Find Your Learning Path
              </Link>
              <Link to="/contact" className="btn-outline-light w-full" data-testid="mobile-cta-contact">
                Talk to Vantage
              </Link>
            </div>
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
}
